import React from "react";
import { Button, Select, TextField } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import ButtonV2 from "elements/ButtonV2";

const MaritalStatus = () => {
	const {
		flowId,
		next,
		maritalStatusValue,
		setMaritalStatusValue,
		specialFieldsValue,
		setSpecialFieldsValue,
		additionalOwnerFirstNameValue,
		setAdditionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		setAdditionalOwnerLastNameValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Marital_Status_Answer,
			data: {
				"Ownership ?": maritalStatusValue,
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		const trackingEventData = {
			"Ownership ?": maritalStatusValue,
		};
		next({
			trackingEventData,
		});
	};

	const options = React.useMemo(() => {
		if (flowId === "NEW_YORK_EXEMPTION") {
			return [
				{ label: "Married", value: "Married" },
				{ label: "Single", value: "Single" },
			];
		} else if (flowId === "ILLINOIS_EXEMPTION") {
			return [
				{ label: "Married/Living Together", value: "Married/Living Together" },
				{
					label: "Married/Not Living Together",
					value: "Married/Not Living Together",
				},
				{
					label: "Single/Divorce/Widow(er)",
					value: "Single/Divorce/Widow(er)",
				},
			];
		} else if (flowId === "FLORIDA_EXEMPTION") {
			return [
				{ label: "Single", value: "Single" },
				{
					label: "Married",
					value: "Married",
				},
				{
					label: "Divorced",
					value: "Divorced",
				},
				{
					label: "Widowed",
					value: "Widowed",
				},
			];
		} else {
			return [
				{ label: "Married", value: "Married" },
				{ label: "Married filing separate", value: "Married filing separate" },
				{ label: "Single", value: "Single" },
			];
		}
	}, [flowId]);

	const btnDisabled = React.useMemo(() => {
		if (flowId === "ILLINOIS_EXEMPTION") {
			if (maritalStatusValue === "Married/Living Together") {
				return !additionalOwnerFirstNameValue || !additionalOwnerLastNameValue;
			}
			if (maritalStatusValue === "Married/Not Living Together") {
				return (
					!additionalOwnerFirstNameValue ||
					!additionalOwnerLastNameValue ||
					!specialFieldsValue?.spouse_address?.address ||
					!specialFieldsValue?.spouse_address?.city ||
					!specialFieldsValue?.spouse_address?.state ||
					!specialFieldsValue?.spouse_address?.zip_code
				);
			}
		}
		return !maritalStatusValue;
	}, [
		flowId,
		maritalStatusValue,
		specialFieldsValue,
		additionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
	]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>What is your marital status?</p>
				</div>
				<Select
					className="exemptions-card-input"
					required
					value={maritalStatusValue ?? ""}
					onChange={e => setMaritalStatusValue(e.target.value)}>
					<option value="" hidden disabled>
						Select an Answer
					</option>
					{options.map(({ label, value }) => (
						<option key={label} value={value}>
							{label}
						</option>
					))}
				</Select>
			</div>
			{flowId === "ILLINOIS_EXEMPTION" &&
				["Married/Living Together", "Married/Not Living Together"].includes(
					maritalStatusValue ?? ""
				) && (
					<div className="property-profile-question-card exemption-survey-card">
						<div className="property-profile-question-card-title-container">
							<p>Spouse Details</p>
						</div>
						<div className="exemptions-input-flex-container">
							<TextField
								required
								label="First Name"
								placeholder="First Name"
								containerClassName="exemptions-card-input"
								value={additionalOwnerFirstNameValue ?? ""}
								onChange={e => {
									setAdditionalOwnerFirstNameValue(e.target.value);
								}}
							/>
							<TextField
								required
								value={additionalOwnerLastNameValue ?? ""}
								onChange={e => {
									setAdditionalOwnerLastNameValue(e.target.value);
								}}
								label="Last Name"
								placeholder="Last Name"
								containerClassName="exemptions-card-input"
							/>
						</div>
						{maritalStatusValue === "Married/Not Living Together" && (
							<>
								<TextField
									required
									label="Street Address"
									placeholder="Street Address"
									value={specialFieldsValue?.spouse_address?.address ?? ""}
									containerClassName="mt-2"
									onChange={e => {
										setSpecialFieldsValue({
											spouse_address: {
												...specialFieldsValue?.spouse_address,
												address: e.target.value,
											},
										});
									}}
								/>
								<TextField
									placeholder="Apt/Suite/Unit"
									value={specialFieldsValue?.spouse_address?.address2 ?? ""}
									containerClassName="appeal-address-address2"
									onChange={e => {
										setSpecialFieldsValue({
											spouse_address: {
												...specialFieldsValue?.spouse_address,
												address2: e.target.value,
											},
										});
									}}
								/>
								<TextField
									required
									label="City"
									placeholder="City"
									value={specialFieldsValue?.spouse_address?.city ?? ""}
									containerClassName="appeal-address-address2"
									onChange={e => {
										setSpecialFieldsValue({
											spouse_address: {
												...specialFieldsValue?.spouse_address,
												city: e.target.value,
											},
										});
									}}
								/>
								<div className="exemptions-input-flex-container">
									<TextField
										required
										label="State"
										placeholder="State"
										containerClassName="exemptions-card-input"
										value={specialFieldsValue?.spouse_address?.state ?? ""}
										onChange={e => {
											setSpecialFieldsValue({
												spouse_address: {
													...specialFieldsValue?.spouse_address,
													state: e.target.value,
												},
											});
										}}
									/>
									<TextField
										required
										label="Zip"
										placeholder="Zip"
										value={specialFieldsValue?.spouse_address?.zip_code ?? ""}
										onChange={e => {
											setSpecialFieldsValue({
												spouse_address: {
													...specialFieldsValue?.spouse_address,
													zip_code: e.target.value,
												},
											});
										}}
										containerClassName="exemptions-card-input"
									/>
								</div>
							</>
						)}
					</div>
				)}
			<ButtonV2 size="large" disabled={btnDisabled} type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default MaritalStatus;
